import { addTaskTypename, isSimpleRecurrence, isTaskCloseableByMember } from '@frontend/api-client';
import {
  useCallback,
  useEffect,
  useFeatureIsOn,
  useMemo,
  useState,
  useTempFeatureFlag,
  useVisibilityController,
} from '@frontend/react';
import type { DropdownMenuProps } from '@frontend/web-react';
import { DropdownMenu, Icon, LoadingSpinnerPanel, PageContainer, XStack, YStack } from '@frontend/web-react';
import {
  CloseTaskButton,
  CloseTaskModal,
  FeedbackButton,
  HeaderButton,
  ShareTaskModal,
  TaskAttachmentModal,
  TaskDetailHeader,
} from '@frontend/web/components';
import { TaskDetailModal } from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailModal';
import {
  TaskDetailChatTab,
  TaskDetailSummaryTab,
  TaskDetailTabHeader,
} from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailTab';
import { TaskLikeCard } from '@frontend/web/components/DomainSpecific/Tasks/TaskLikeCard';
import { TaskSummaryModal } from '@frontend/web/components/DomainSpecific/Tasks/TaskSummaryModal';
import {
  useAccessControl,
  useAnalytics,
  useLazyRetrieveHouseholdQuery,
  useListRecurrencesQuery,
  useRetrieveClientQuery,
  useRetrieveTaskQuery,
} from '@frontend/web/hooks';
import { createFileRoute, useRouter } from '@frontend/web/utils';

const NewTask = () => {
  const { taskId } = Route.useParams();
  const { track } = useAnalytics();
  const { data: task, refetch: refetchTask } = useRetrieveTaskQuery(taskId, { refetchOnMountOrArgChange: true });
  const { isClientSubscribed } = useAccessControl();
  const [isTaskDetailModalOpen, { close: closeTaskDetailModal, open: openTaskDetailModal }] = useVisibilityController();
  const [isTaskSummaryModalOpen, { close: closeTaskSummaryModal, open: openTaskSummaryModal }] =
    useVisibilityController();
  const [isCloseTaskModalOpen, { close: closeCloseTaskModal, open: openCloseTaskModal }] = useVisibilityController();
  const [isShareTaskModalOpen, { close: closeShareTaskModal, open: openShareTaskModal }] = useVisibilityController();
  const [isSearchOpen, { close: closeSearch, toggle: toggleSearch }] = useVisibilityController();

  useEffect(() => {
    const handleFocus = () => {
      refetchTask();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchTask]);

  const handleOpenSummary = useCallback(() => {
    track({ data: { taskId }, event: 'taskOverviewViewed', topic: 'tasks' });
    openTaskSummaryModal();
  }, [openTaskSummaryModal, taskId, track]);

  const handleToggleSearch = useCallback(() => {
    track({ data: { taskId }, event: 'taskMessageSearchSelected', topic: 'tasks' });
    toggleSearch();
  }, [taskId, toggleSearch, track]);

  return (
    <PageContainer className="bg-trueWhite" isScrollable={false} padding="none">
      <TaskDetailHeader
        onCloseTask={openCloseTaskModal}
        onOpenSummary={handleOpenSummary}
        onShareTask={openShareTaskModal}
        onShareWithHousehold={openTaskDetailModal}
        onToggleSearch={handleToggleSearch}
        task={task}
      />
      {task ? (
        <TaskDetailChatTab
          isClientSubscribed={isClientSubscribed}
          isSearchOpen={isSearchOpen}
          onCloseSearch={closeSearch}
        />
      ) : (
        <LoadingSpinnerPanel fullScreen />
      )}
      <TaskDetailModal isOpen={isTaskDetailModalOpen} onClose={closeTaskDetailModal} task={task} />
      <TaskSummaryModal isOpen={isTaskSummaryModalOpen} onClose={closeTaskSummaryModal} task={task} />
      {task ? <CloseTaskModal isOpen={isCloseTaskModalOpen} onClose={closeCloseTaskModal} task={task} /> : null}
      <ShareTaskModal isOpen={isShareTaskModalOpen} onClose={closeShareTaskModal} taskId={taskId} />
    </PageContainer>
  );
};

const OldTask = () => {
  const { taskId } = Route.useParams();
  const { track } = useAnalytics();
  const { data: task, refetch: refetchTask } = useRetrieveTaskQuery(taskId, { refetchOnMountOrArgChange: true });
  const [activeTab, setActiveTab] = useState<'chat' | 'summary'>('chat');
  const { data: client } = useRetrieveClientQuery();
  const { isClientSubscribed } = useAccessControl();
  const isInTaskSearchEnabled = useTempFeatureFlag('is_in_task_search_enabled');
  const [retrieveHousehold, { data: household }] = useLazyRetrieveHouseholdQuery();
  const { data: recurrences = [] } = useListRecurrencesQuery();
  const [isTaskDetailModalOpen, { close: closeTaskDetailModal, open: openTaskDetailModal }] = useVisibilityController();
  const [isTaskAttachmentModalOpen, { close: closeTaskAttachmentModalModal, open: openTaskAttachmentModalModal }] =
    useVisibilityController();
  const [isCloseTaskModalOpen, { close: closeCloseTaskModal, open: openCloseTaskModal }] = useVisibilityController();
  const [isSearchOpen, { close: closeSearch, toggle: toggleSearch }] = useVisibilityController();
  const router = useRouter();

  const nextTriggerAt = recurrences.filter(isSimpleRecurrence).find((r) => r.taskId === taskId)?.nextTriggerAt;

  const handleToggleSearch = useCallback(() => {
    track({ data: { taskId }, event: 'taskMessageSearchSelected', topic: 'tasks' });
    toggleSearch();
  }, [taskId, toggleSearch, track]);

  useEffect(() => {
    if (client?.household) {
      retrieveHousehold(client.household);
    }
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      refetchTask();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchTask]);

  const dropdownMenuOptions = useMemo(() => {
    const options: DropdownMenuProps['layout'] = [];
    if (household?.members?.length) {
      // eslint-disable-next-line fp/no-mutating-methods
      options.push({
        icon: 'IconShare2',
        label: 'Share task',
        onClick: () => openTaskDetailModal(),
        type: 'item',
      });
    }
    if (task?.attachments?.length) {
      // eslint-disable-next-line fp/no-mutating-methods
      options.push({
        icon: 'IconFile',
        label: 'Attachments',
        onClick: () => {
          if (task?.attachments.length) {
            openTaskAttachmentModalModal();
          }
        },
        type: 'item',
      });
    }
    return options;
  }, [household, task, isClientSubscribed]);

  return (
    <PageContainer className="bg-trueWhite" isScrollable={false} padding="none">
      <XStack className="flex items-center p-lg justify-between">
        <HeaderButton iconName="IconChevronLeft" onClick={router.history.back} />
        <YStack className="grow-1">
          {task ? (
            <TaskLikeCard isPressDisabled nextTriggerAt={nextTriggerAt} taskLikeObject={addTaskTypename(task)} />
          ) : null}
        </YStack>
        <XStack alignItems="center" gapX="sm">
          {task && isInTaskSearchEnabled ? (
            <Icon accessibilityLabel="Search messages" name="IconSearch" onClick={handleToggleSearch} />
          ) : null}
          {task && isTaskCloseableByMember(task) && isClientSubscribed ? (
            <CloseTaskButton onClick={openCloseTaskModal} />
          ) : null}
          <FeedbackButton taskId={taskId} />
          {dropdownMenuOptions.length ? (
            <DropdownMenu layout={dropdownMenuOptions}>
              <Icon
                accessibilityLabel="Task options"
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="bg-neutral-100 items-center justify-center size-[36px] rounded-full"
                name="IconDots"
              />
            </DropdownMenu>
          ) : null}
        </XStack>
      </XStack>
      <TaskDetailTabHeader activeTab={activeTab} setActiveTab={setActiveTab} />
      {task ? (
        activeTab === 'chat' ? (
          <TaskDetailChatTab
            isClientSubscribed={isClientSubscribed}
            isSearchOpen={isSearchOpen}
            onCloseSearch={closeSearch}
          />
        ) : (
          <TaskDetailSummaryTab task={task} />
        )
      ) : (
        <LoadingSpinnerPanel fullScreen />
      )}
      <TaskDetailModal isOpen={isTaskDetailModalOpen} onClose={closeTaskDetailModal} task={task} />
      {task ? <CloseTaskModal isOpen={isCloseTaskModalOpen} onClose={closeCloseTaskModal} task={task} /> : null}
      <TaskAttachmentModal isOpen={isTaskAttachmentModalOpen} onClose={closeTaskAttachmentModalModal} task={task} />
    </PageContainer>
  );
};

const Task = () => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');

  if (isGameplanPricingEnabled) {
    return <NewTask />;
  } else {
    return <OldTask />;
  }
};

export const Route = createFileRoute('/app/task/$taskId')({
  component: Task,
});
